import { inject, Injectable } from '@angular/core';
import { isEmpty } from '@app/core/functions/isEmpty';
import { LogService } from '@app/core/services/log.service';
import { CLIENT_KEY, GOOGLE_PRODUCT_FEED_CLIENT_VALUE, TIKTOK_PRODUCT_FEED_CLIENT_VALUE } from '@app/exports/models/constants/export-http-body.constants';
import { ExportTypeEnum } from '@app/exports/models/enums/export-type.enum';
import { ExportInterface } from '@app/exports/models/interfaces/export.interface';
import { ExportUtilitiesService } from '@app/exports/services/export-utilities.service';

@Injectable({
    providedIn: 'root'
})
export class EDSUtilsService {

    static readonly EDS_WHITELISTED_EXPORT_WEBHOOK_CLIENTS: ReadonlySet<string> = new Set<string>(
        [
            'GoogleRegionAvailabilityFeed',
            'FacebookProductFeedFTPWithoutDisable',
            'AmazonSellingPartnerProductFeed',
            'AmazonSellingPartnerInventoryLoader',
            'AmazonSellingPartnerInventoryAndPrice',
            'AmazonTodayInventoryFeed',
            'AmazonInventoryAndPriceSellerCentral',
            'WalmartProductFeedV4',
            'WalmartPricesFeed',
            'WalmartInventoryFeed',
            'WalmartFacilitiesFeed',
            'WalmartBulkInventoryFeed',
            'eBayAPIProductFeed',
            'EbayAPIPriceInventoryFeed',
            'TargetProductFeedWithoutDisable',
            'TargetUpdateQuantityAndPrice',
            'TargetUpdateQuantity',
            'TargetUpdatePrice',
            'MiraklExportProducts',
            'MiraklExportProductsOffers',
            'MiraklExportOffers',
            'TikTokPriceInventoryFeed',
            'WalmartDisableFeedV4',
            'WalmartPromotionFeed',
            'WalmartSetupByMatchV4',
            'WalmartWFSItemConversion',
            'WalmartWFSItemSetup',
            'WalmartSkuNodeTemplateAssociationFeed',
            'eBayAPIEndListingsFeed',
            'eBayPromotionListingsFeed',
            'eBayAPIAdsPromotedListingsFeed',
            'TargetDisableProducts',
            'TargetUpdateExternalId',
            'TargetUpdateLogistics',
            'MiraklDeleteProductsOffers',
            'MiraklDeleteOffers',
            'TikTokDeleteFeed'
        ]
    );

    private readonly logService: LogService = inject(LogService);
    private readonly exportUtilitiesService: ExportUtilitiesService = inject(ExportUtilitiesService);

    /**
     * Returns if this is a valid export to show in the Export Triggers page for an EDS database.
     * Checks that the `protocol` is `'webhook'` and the `protocol_info.http_body.client` is whitelisted.
     * @param e Export
     * @returns boolean
    */
    isValidExportForEDS(e: Partial<ExportInterface>): boolean {
        // sanity check - export should be truthy
        if (!e || isEmpty(e.id) || Number(e.id) <= 0 || e.export_type === ExportTypeEnum.General) {
            return false;
        }

        // check protocol
        switch (e.protocol) {
            // show export for ftp, sftp, ftps
            case 'ftp':
            case 'sftp':
            case 'ftps':
                return true;

            // continue webhook checks below
            case 'webhook':
                break;

            // hide unknown export protocols
            default:
                this.logService.warn('[EDSUtilsService:filterValidExportsForEDS] Unknown export protocol:', e.protocol, e);
                return false;
        }

        // this is a webhook export

        // parse json of http body string
        const parsed: Record<string, string | number | boolean> = this.exportUtilitiesService.parseWebhookHTTPBody(e);

        if (!parsed) {
            return false;
        }

        // no client defined in body - still show this export for testing and demos
        // https://feedonomics.atlassian.net/browse/FP-9307?focusedCommentId=142234
        if (!('client' in parsed)) {
            return true;
        }

        // client is supposed to be a string
        if (typeof (parsed[CLIENT_KEY]) !== 'string') {
            return false;
        }

        // check if this is a whitelisted client
        // https://feedonomics.atlassian.net/browse/FP-9307?focusedCommentId=141641
        if (EDSUtilsService.EDS_WHITELISTED_EXPORT_WEBHOOK_CLIENTS.has(parsed[CLIENT_KEY])) {
            return true;
        }

        // some clients require additional conditions
        switch (parsed[CLIENT_KEY]) {
            case GOOGLE_PRODUCT_FEED_CLIENT_VALUE:
                return parsed.partial_update === true;

            case TIKTOK_PRODUCT_FEED_CLIENT_VALUE:
                return parsed.disable_listings === false;

            default:
                return false;
        }
    }
}
